import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchFrom, IMG_W500, IMG_BASE } from '../utils/tmdb';

const Row = ({ title, fetchPath, items: providedItems, isLarge = false, onSelect }) => {
  const [items, setItems] = useState(providedItems || []);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (providedItems !== undefined) {
      setItems(providedItems);
      return;
    }
    if (!fetchPath) return;
    (async () => {
      const data = await fetchFrom(fetchPath);
      setItems(data.filter((i) => (isLarge ? i.poster_path : (i.backdrop_path || i.poster_path))));
    })();
  }, [fetchPath, providedItems, isLarge]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      const w = scrollRef.current.clientWidth;
      scrollRef.current.scrollBy({ left: dir === 'left' ? -w * 0.9 : w * 0.9, behavior: 'smooth' });
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="pl-4 md:pl-14 mt-8 group/row relative">
      <h2 className="text-white text-lg md:text-2xl font-semibold mb-3">{title}</h2>

      <div className="relative">
        <button
          type="button"
          onClick={() => scroll('left')}
          className="absolute left-0 top-0 bottom-0 z-20 w-10 md:w-14 bg-black/40 hover:bg-black/70 flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
          aria-label="scroll left"
        >
          <ChevronLeft className="w-8 h-8 text-white pointer-events-none" />
        </button>

        <div
          ref={scrollRef}
          className="flex gap-2 overflow-x-scroll scrollbar-hide scroll-smooth pr-4 md:pr-14 py-6"
        >
          {items.map((item) => {
            const isFullUrl = (s) => typeof s === 'string' && s.startsWith('http');
            let src = '';
            if (isLarge) {
              if (item.poster_path) src = isFullUrl(item.poster_path) ? item.poster_path : `${IMG_W500}${item.poster_path}`;
              else if (item.backdrop_path) src = isFullUrl(item.backdrop_path) ? item.backdrop_path : `${IMG_BASE}${item.backdrop_path}`;
            } else {
              if (item.backdrop_path) src = isFullUrl(item.backdrop_path) ? item.backdrop_path : `${IMG_BASE}${item.backdrop_path}`;
              else if (item.poster_path) src = isFullUrl(item.poster_path) ? item.poster_path : `${IMG_W500}${item.poster_path}`;
            }
            if (!src) return null;
            return (
              <div
                key={`${item.media_type || 'x'}-${item.id}`}
                onClick={() => onSelect(item)}
                className={`relative flex-shrink-0 cursor-pointer rounded overflow-hidden transition-transform duration-300 ease-out hover:scale-110 hover:z-30 hover:shadow-2xl ${
                  isLarge ? 'w-[160px] md:w-[200px]' : 'w-[240px] md:w-[300px]'
                }`}
              >
                <img
                  src={src}
                  alt={item.name || item.title}
                  className={`w-full object-cover ${isLarge ? 'h-[280px] md:h-[300px]' : 'h-[135px] md:h-[170px]'}`}
                  loading="lazy"
                />
                {item.stream_url && (
                  <span className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                    Full Movie
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => scroll('right')}
          className="absolute right-0 top-0 bottom-0 z-20 w-10 md:w-14 bg-black/40 hover:bg-black/70 flex items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity"
          aria-label="scroll right"
        >
          <ChevronRight className="w-8 h-8 text-white pointer-events-none" />
        </button>
      </div>
    </div>
  );
};

export default Row;
